import { useEffect, useRef, useState } from 'react';
import { track } from '../lib/track';

// Diwali welcome offer pop-up: ₹300 off a first order, new-stock ACs only.
// On the Buy AC page and the Daikin product pages, not on clearance pages.
//
// Shown once per visitor, a few seconds after they arrive, and never again
// once closed. The visitor gives their mobile number and gets a code that
// works only with that number; it is saved where checkout reads offers
// (cc_offer), so it is applied without typing anything. Every rule is
// checked again on the server at payment (api/_welcome.js) — this pop-up
// decides only what is shown.

const ENDS = new Date('2026-11-10T23:59:59+05:30');
const SEEN_KEY = 'cc_welcome_seen';
const OFFER_KEY = 'cc_offer';
const DELAY_MS = 8000;

function canShow() {
  try {
    if (Date.now() >= ENDS.getTime()) return false;
    if (localStorage.getItem(SEEN_KEY)) return false;
    if (localStorage.getItem(OFFER_KEY)) return false;
    return true;
  } catch {
    return false; // no storage: it could not remember it was shown
  }
}

export default function WelcomeOffer() {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState('');
  const [state, setState] = useState({ kind: 'idle', message: '' });
  const inputRef = useRef(null);

  useEffect(() => {
    if (!canShow()) return undefined;
    const t = setTimeout(() => {
      if (!canShow()) return;
      try { localStorage.setItem(SEEN_KEY, new Date().toISOString()); } catch { /* shown once anyway */ }
      setOpen(true);
      track('welcome_offer_shown', {});
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    const f = setTimeout(() => inputRef.current?.focus(), 50);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(f); };
  }, [open]);

  if (!open) return null;

  const valid = /^[6-9]\d{9}$/.test(mobile);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setState({ kind: 'bad', message: 'Enter a 10-digit mobile number.' }); return; }
    setState({ kind: 'working', message: '' });
    try {
      const r = await fetch('/api/welcome-offer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile }),
      });
      const data = await r.json().catch(() => ({}));
      if (data.ok) {
        try {
          localStorage.setItem(OFFER_KEY, JSON.stringify({
            code: data.code, amount: data.amount, mobile: data.mobile, expiresAt: data.expiresAt, welcome: true,
          }));
        } catch { /* code is still shown below */ }
        setState({ kind: 'done', message: data.code });
        track('welcome_offer_claimed', {});
        return;
      }
      if (data.reason) { setState({ kind: 'no', message: data.message }); return; }
      setState({ kind: 'bad', message: data.error || 'Could not create your code. Try again.' });
    } catch {
      setState({ kind: 'bad', message: 'Could not connect. Check your internet and try again.' });
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 sm:items-center sm:p-4"
      onClick={() => setOpen(false)}>
      <div role="dialog" aria-modal="true" aria-labelledby="welcome-offer-title"
        className="relative w-full max-w-md rounded-t-2xl bg-white p-5 pb-6 shadow-xl sm:rounded-2xl sm:p-6"
        onClick={(e) => e.stopPropagation()}>
        <button type="button" aria-label="Close" onClick={() => setOpen(false)}
          className="absolute right-3 top-3 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900">
          <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M5 5l10 10M15 5L5 15" />
          </svg>
        </button>

        <p className="text-xs font-medium text-blue-700">Diwali welcome offer</p>

        {state.kind === 'done' ? (
          <>
            <h2 id="welcome-offer-title" className="mt-1 text-xl font-semibold text-gray-900">Your ₹300 code is ready</h2>
            <p className="mt-3 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 py-3 text-center font-mono text-lg font-semibold tracking-wide text-blue-800">
              {state.message}
            </p>
            <p className="mt-3 text-sm text-gray-600">
              It is saved on this phone and comes off at checkout when you pay with {mobile.slice(0, 5)} {mobile.slice(5)}.
            </p>
            <button type="button" onClick={() => setOpen(false)}
              className="mt-5 w-full rounded-lg bg-blue-600 py-3 text-sm font-medium text-white transition hover:bg-blue-700">
              Continue shopping
            </button>
          </>
        ) : state.kind === 'no' ? (
          <>
            <h2 id="welcome-offer-title" className="mt-1 text-xl font-semibold text-gray-900">Welcome back</h2>
            <p className="mt-2 text-sm text-gray-600">{state.message}</p>
            <button type="button" onClick={() => setOpen(false)}
              className="mt-5 w-full rounded-lg bg-blue-600 py-3 text-sm font-medium text-white transition hover:bg-blue-700">
              Continue shopping
            </button>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <h2 id="welcome-offer-title" className="mt-1 text-xl font-semibold text-gray-900">₹300 off your first AC order</h2>
            <p className="mt-2 text-sm text-gray-600">
              Enter your mobile number to get your code. It works only with this number.
            </p>
            <label htmlFor="welcome-mobile" className="mt-4 block text-[13px] font-medium text-gray-700">Mobile number</label>
            <div className={`mt-1 flex items-center rounded-lg border ${state.kind === 'bad' ? 'border-red-500' : 'border-gray-300'} focus-within:border-blue-500`}>
              <span className="pl-3 text-sm text-gray-500">+91</span>
              <input id="welcome-mobile" ref={inputRef} type="tel" inputMode="numeric" autoComplete="tel-national"
                value={mobile} maxLength={10} placeholder="98765 43210"
                onChange={(e) => { setMobile(e.target.value.replace(/\D/g, '').slice(0, 10)); if (state.kind === 'bad') setState({ kind: 'idle', message: '' }); }}
                className="w-full rounded-lg border-0 bg-transparent px-2 py-3 text-base text-gray-900 outline-none" />
            </div>
            {state.kind === 'bad' && <p className="mt-1.5 text-[13px] text-red-600">{state.message}</p>}
            <button type="submit" disabled={state.kind === 'working'}
              className="mt-4 w-full rounded-lg bg-blue-600 py-3 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-70">
              {state.kind === 'working' ? 'Getting your code…' : 'Get my ₹300 code'}
            </button>
            <p className="mt-3 text-[12px] leading-relaxed text-gray-500">
              For first orders on new-stock ACs, one per customer and address. Valid till 10 Nov 2026. Cannot be combined with other offers.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
